import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import {
  FiSearch,
  FiRefreshCw,
  FiX,
  FiFilter,
  FiTag,
  FiChevronDown,
  FiSliders,
  FiChevronUp,
} from "react-icons/fi";


function MealsFilter({
  onFilterChange,
  categories = [],
  initialFilters = {},
}) {
  const {
    t,
    i18n,
  } = useTranslation();


  /* =====================================
      MOBILE FILTER
  ====================================== */

  const [
    mobileFiltersOpen,
    setMobileFiltersOpen,
  ] = useState(false);


  /* =====================================
      LANGUAGE
  ====================================== */

  const isArabic =
    i18n.language === "ar" ||
    i18n.language?.startsWith("ar");


  /* =====================================
      DEFAULT FILTERS
  ====================================== */

  const defaultFilters = {
    search: "",
    categoryId: "",
    priceMin: "",
    priceMax: "",
    sortBy: "default",
  };


  /* =====================================
      LOCAL FILTERS
  ====================================== */

  const [
    filters,
    setFilters,
  ] = useState({
    ...defaultFilters,
    ...initialFilters,
  });


  /* =====================================
      SYNC WITH PARENT
  ====================================== */

  useEffect(() => {
    setFilters({
      ...defaultFilters,
      ...initialFilters,
    });
  }, [
    initialFilters.search,
    initialFilters.categoryId,
    initialFilters.priceMin,
    initialFilters.priceMax,
    initialFilters.sortBy,
  ]);


  /* =====================================
      SELECTED CATEGORY
  ====================================== */

  const selectedCategory =
    useMemo(
      () =>
        categories.find(
          (category) =>
            category?._id ===
            initialFilters.categoryId
        ),
      [
        categories,
        initialFilters.categoryId,
      ]
    );


  /* =====================================
      CHANGE
  ====================================== */

  const handleChange =
    (e) => {
      const {
        name,
        value,
      } = e.target;

      setFilters(
        (prev) => ({
          ...prev,
          [name]: value,
        })
      );
    };


  /* =====================================
      CLOSE ON MOBILE
  ====================================== */

  const closeMobileFilter =
    () => {
      if (
        typeof window !==
          "undefined" &&
        window.innerWidth <
          768
      ) {
        setMobileFiltersOpen(
          false
        );
      }
    };


  /* =====================================
      SEARCH
  ====================================== */

  const handleSearch =
    () => {
      const preparedFilters = {
        ...filters,

        search:
          filters.search.trim(),

        priceMin:
          filters.priceMin ===
          ""
            ? ""
            : Number(
                filters.priceMin
              ),

        priceMax:
          filters.priceMax ===
          ""
            ? ""
            : Number(
                filters.priceMax
              ),
      };


      if (
        preparedFilters.priceMin !==
          "" &&
        preparedFilters.priceMax !==
          "" &&
        preparedFilters.priceMin >
          preparedFilters.priceMax
      ) {
        return;
      }


      setFilters(
        preparedFilters
      );

      onFilterChange(
        preparedFilters
      );


      /*
       * بعد البحث على الجوال
       * نغلق الفلتر تلقائياً.
       */
      closeMobileFilter();
    };


  /* =====================================
      RESET
  ====================================== */

  const handleReset =
    () => {
      const resetFilters = {
        ...defaultFilters,
      };


      setFilters(
        resetFilters
      );

      onFilterChange(
        resetFilters
      );


      closeMobileFilter();
    };


  /* =====================================
      CLEAR FILTER
  ====================================== */

  const clearFilter =
    (field) => {
      const nextFilters = {
        ...filters,

        [field]:
          field === "sortBy"
            ? "default"
            : "",
      };


      setFilters(
        nextFilters
      );

      onFilterChange(
        nextFilters
      );
    };


  /* =====================================
      CLEAR PRICE
  ====================================== */

  const clearPriceRange =
    () => {
      const nextFilters = {
        ...filters,

        priceMin: "",
        priceMax: "",
      };


      setFilters(
        nextFilters
      );

      onFilterChange(
        nextFilters
      );
    };


  /* =====================================
      ENTER
  ====================================== */

  const handleKeyDown =
    (e) => {
      if (
        e.key === "Enter"
      ) {
        e.preventDefault();

        handleSearch();
      }
    };


  /* =====================================
      STATUS
  ====================================== */

  const hasDraftFilters =
    Boolean(
      filters.search ||
        filters.categoryId ||
        filters.priceMin !==
          "" ||
        filters.priceMax !==
          "" ||
        filters.sortBy !==
          "default"
    );


  const hasActiveFilters =
    Boolean(
      initialFilters.search ||
        initialFilters.categoryId ||
        (
          initialFilters.priceMin !==
            "" &&
          initialFilters.priceMin !==
            undefined
        ) ||
        (
          initialFilters.priceMax !==
            "" &&
          initialFilters.priceMax !==
            undefined
        ) ||
        (
          initialFilters.sortBy &&
          initialFilters.sortBy !==
            "default"
        )
    );


  /* =====================================
      ACTIVE FILTER COUNT
  ====================================== */

  const activeFiltersCount =
    [
      Boolean(
        initialFilters.search
      ),

      Boolean(
        initialFilters.categoryId
      ),

      Boolean(
        initialFilters.priceMin !==
          "" &&
          initialFilters.priceMin !==
            undefined
      ),

      Boolean(
        initialFilters.priceMax !==
          "" &&
          initialFilters.priceMax !==
            undefined
      ),

      Boolean(
        initialFilters.sortBy &&
          initialFilters.sortBy !==
            "default"
      ),
    ].filter(Boolean).length;


  return (
    <div className="w-full">

      {/* =================================
          MOBILE FILTER BUTTON
      ================================= */}

      <button
        type="button"

        onClick={() =>
          setMobileFiltersOpen(
            (prev) =>
              !prev
          )
        }

        className="
          md:hidden

          flex

          w-full
          h-[50px]

          items-center
          justify-between

          rounded-[1rem]

          border
          border-color-1/15

          bg-back-secondary/70
          dark:bg-back-darkprimery/70

          px-4

          transition-all

          active:scale-[0.99]
        "
      >

        <div
          className="
            flex
            items-center

            gap-3
          "
        >

          <div
            className="
              relative

              flex

              w-9
              h-9

              items-center
              justify-center

              rounded-xl

              bg-color-1/10

              text-color-1
            "
          >
            <FiSliders
              size={16}
            />


            {activeFiltersCount >
              0 && (

              <span
                className="
                  absolute

                  -top-1
                  -end-1

                  flex

                  min-w-[17px]
                  h-[17px]

                  items-center
                  justify-center

                  rounded-full

                  bg-color-1

                  px-1

                  font-kufi
                  text-[8px]
                  font-bold

                  text-white
                "
              >
                {
                  activeFiltersCount
                }
              </span>

            )}

          </div>


          <div
            className="
              text-start
            "
          >

            <span
              className="
                block

                font-kufi
                font-semibold

              text-body

                text-color-2
                dark:text-white
              "
            >
              {t(
                "Filter Meals"
              )}
            </span>


            <span
              className="
                block

                mt-0.5

                font-kufi
                text-[10px]

                text-color-2/40
                dark:text-white/30
              "
            >

              {activeFiltersCount >
              0
                ? `${activeFiltersCount} ${t(
                    "filters applied"
                  )}`
                : t(
                    "Tap to show filters"
                  )}

            </span>

          </div>

        </div>


        <div
          className="
            flex

            w-8
            h-8

            items-center
            justify-center

            rounded-lg

            bg-color-1/[0.07]

            text-color-1
          "
        >
          {mobileFiltersOpen ? (
            <FiChevronUp
              size={16}
            />
          ) : (
            <FiChevronDown
              size={16}
            />
          )}
        </div>

      </button>


      {/* =================================
          FILTER CONTENT
      ================================= */}

      <div
        className={`
          ${
            mobileFiltersOpen
              ? "block"
              : "hidden"
          }

          md:block

          mt-3
          md:mt-0
        `}
      >

        {/* Mobile inner card */}

        <div
          className="
            md:contents

            rounded-[1.2rem]

            border
            border-color-2/[0.07]
            dark:border-white/[0.07]

            bg-back-secondary/40
            dark:bg-back-darkprimery/40

            p-3
          "
        >

          {/* =============================
              GRID
          ============================== */}

          <div
            className="
              grid

              grid-cols-1
              md:grid-cols-2
              xl:grid-cols-4

              2xl:grid-cols-[1.35fr_1fr_.75fr_.75fr_1fr_auto]

              gap-3

              items-end
            "
          >

            {/* =================================
                SEARCH
            ================================= */}

            <FilterField
              label={t(
                "Search"
              )}

              icon={
                <FiSearch
                  size={15}
                />
              }
            >

              <input
                type="text"

                name="search"

                value={
                  filters.search
                }

                onChange={
                  handleChange
                }

                onKeyDown={
                  handleKeyDown
                }

                placeholder={t(
                  "Search meal by Arabic or English name"
                )}

                className="
                  w-full
                  h-full

                  bg-transparent

                  px-1

                  font-kufi

                  text-[11px]
                  md:text-xs

                  text-color-2
                  dark:text-white

                  placeholder:text-color-2/30
                  dark:placeholder:text-white/25

                  outline-none
                "
              />


              {filters.search && (

                <ClearButton
                  onClick={() =>
                    setFilters(
                      (
                        prev
                      ) => ({
                        ...prev,
                        search:
                          "",
                      })
                    )
                  }
                />

              )}

            </FilterField>


            {/* =================================
                CATEGORY
            ================================= */}

            <div className="w-full">

              <label
                className="
                  block

                  mb-2

                  font-kufi
                  font-medium

                  text-[10px]
                  md:text-[11px]

                  text-color-2/60
                  dark:text-white/50
                "
              >
                {t(
                  "Category"
                )}
              </label>


              <div
                className="
                  relative

                  flex

                  h-[48px]

                  items-center

                  overflow-hidden

                  rounded-[1rem]

                  border
                  border-color-2/10
                  dark:border-white/10

                  bg-back-secondary/60
                  dark:bg-back-darkprimery/60

                  transition-all
                  duration-300

                  focus-within:border-color-1/60

                  focus-within:shadow-[0_0_0_3px_rgba(165,119,58,.08)]
                "
              >

                <div
                  className="
                    flex

                    w-11
                    h-full

                    shrink-0

                    items-center
                    justify-center

                    text-color-1
                  "
                >
                  <FiTag
                    size={15}
                  />
                </div>


                <select
                  name="categoryId"

                  value={
                    filters.categoryId
                  }

                  onChange={
                    handleChange
                  }

                  className="
                    relative
                    z-10

                    w-full
                    h-full

                    appearance-none

                    bg-transparent

                    pe-9

                    font-kufi

                    text-[11px]
                    md:text-xs

                    text-color-2
                    dark:text-white

                    outline-none

                    cursor-pointer
                  "
                >

                  <option
                    value=""
                  >
                    {t(
                      "All Categories"
                    )}
                  </option>


                  {categories.map(
                    (
                      category
                    ) => (

                      <option
                        key={
                          category._id
                        }

                        value={
                          category._id
                        }
                      >
                        {isArabic
                          ? category.titleAr
                          : category.titleEn}
                      </option>

                    )
                  )}

                </select>


                <FiChevronDown
                  size={14}

                  className="
                    absolute

                    end-3

                    pointer-events-none

                    text-color-1
                  "
                />

              </div>

            </div>


            {/* =================================
                MIN PRICE
            ================================= */}

            <FilterField
              label={t(
                "Minimum Price"
              )}

              icon={
                <span
                  className="
                    font-kufi
                    text-[9px]
                    font-bold
                  "
                >
                  {t("EGP")}
                </span>
              }
            >

              <input
                type="number"

                name="priceMin"

                min="0"

                value={
                  filters.priceMin
                }

                onChange={
                  handleChange
                }

                onKeyDown={
                  handleKeyDown
                }

                placeholder={t(
                  "Min"
                )}

                className="
                  w-full
                  h-full

                  bg-transparent

                  px-1

                  text-[11px]

                  text-color-2
                  dark:text-white

                  placeholder:text-color-2/30
                  dark:placeholder:text-white/25

                  outline-none

                  [appearance:textfield]

                  [&::-webkit-outer-spin-button]:appearance-none
                  [&::-webkit-inner-spin-button]:appearance-none
                "
              />


              {filters.priceMin !==
                "" && (

                <ClearButton
                  onClick={() =>
                    setFilters(
                      (
                        prev
                      ) => ({
                        ...prev,
                        priceMin:
                          "",
                      })
                    )
                  }
                />

              )}

            </FilterField>


            {/* =================================
                MAX PRICE
            ================================= */}

            <FilterField
              label={t(
                "Maximum Price"
              )}

              icon={
                <span
                  className="
                    font-kufi
                    text-[9px]
                    font-bold
                  "
                >
                  {t("EGP")}
                </span>
              }
            >

              <input
                type="number"

                name="priceMax"

                min="0"

                value={
                  filters.priceMax
                }

                onChange={
                  handleChange
                }

                onKeyDown={
                  handleKeyDown
                }

                placeholder={t(
                  "Max"
                )}

                className="
                  w-full
                  h-full

                  bg-transparent

                  px-1

                  text-[11px]

                  text-color-2
                  dark:text-white

                  placeholder:text-color-2/30
                  dark:placeholder:text-white/25

                  outline-none

                  [appearance:textfield]

                  [&::-webkit-outer-spin-button]:appearance-none
                  [&::-webkit-inner-spin-button]:appearance-none
                "
              />


              {filters.priceMax !==
                "" && (

                <ClearButton
                  onClick={() =>
                    setFilters(
                      (
                        prev
                      ) => ({
                        ...prev,
                        priceMax:
                          "",
                      })
                    )
                  }
                />

              )}

            </FilterField>


            {/* =================================
                SORT
            ================================= */}

            <div className="w-full">

              <label
                className="
                  block

                  mb-2

                  font-kufi
                  font-medium

                  text-[10px]
                  md:text-[11px]

                  text-color-2/60
                  dark:text-white/50
                "
              >
                {t(
                  "Sort By"
                )}
              </label>


              <div
                className="
                  relative

                  flex

                  h-[48px]

                  items-center

                  overflow-hidden

                  rounded-[1rem]

                  border
                  border-color-2/10
                  dark:border-white/10

                  bg-back-secondary/60
                  dark:bg-back-darkprimery/60

                  focus-within:border-color-1/60

                  focus-within:shadow-[0_0_0_3px_rgba(165,119,58,.08)]
                "
              >

                <div
                  className="
                    flex

                    w-11
                    h-full

                    shrink-0

                    items-center
                    justify-center

                    text-color-1
                  "
                >
                  <FiSliders
                    size={15}
                  />
                </div>


                <select
                  name="sortBy"

                  value={
                    filters.sortBy
                  }

                  onChange={
                    handleChange
                  }

                  className="
                    relative
                    z-10

                    w-full
                    h-full

                    appearance-none

                    bg-transparent

                    pe-9

                    font-kufi

                    text-[10px]
                    md:text-[11px]

                    text-color-2
                    dark:text-white

                    outline-none

                    cursor-pointer
                  "
                >

                  <option
                    value="default"
                  >
                    {t(
                      "Default"
                    )}
                  </option>


                  <option
                    value="price_asc"
                  >
                    {t(
                      "Price Low to High"
                    )}
                  </option>


                  <option
                    value="price_desc"
                  >
                    {t(
                      "Price High to Low"
                    )}
                  </option>

                </select>


                <FiChevronDown
                  size={14}

                  className="
                    absolute

                    end-3

                    pointer-events-none

                    text-color-1
                  "
                />

              </div>

            </div>


            {/* =================================
                ACTIONS
            ================================= */}

            <div
              className="
                flex

                md:col-span-2
                xl:col-span-4
                2xl:col-span-1

                items-center

                gap-2
              "
            >

              {/* Search */}

              <button
                type="button"

                onClick={
                  handleSearch
                }

                className="
                  flex

                  h-[48px]

                  flex-1
                  2xl:flex-none

                  items-center
                  justify-center

                  gap-2

                  rounded-[1rem]

                  bg-color-1

                  px-5

                  font-kufi
                  font-semibold

                  text-[11px]

                  text-white

                  whitespace-nowrap

                  transition-all

                  hover:bg-color-2

                  hover:shadow-[0_8px_22px_rgba(165,119,58,.22)]

                  active:scale-[0.98]
                "
              >
                <FiSearch
                  size={15}
                />

                {t(
                  "Search"
                )}
              </button>


              {/* Reset */}

              <button
                type="button"

                onClick={
                  handleReset
                }

                disabled={
                  !hasDraftFilters &&
                  !hasActiveFilters
                }

                title={
                  t("Reset")
                }

                className="
                  flex

                  w-[48px]
                  h-[48px]

                  shrink-0

                  items-center
                  justify-center

                  rounded-[1rem]

                  border
                  border-color-1/15

                  bg-color-1/5

                  text-color-1

                  transition-all

                  hover:border-color-1
                  hover:bg-color-1
                  hover:text-white

                  disabled:cursor-not-allowed
                  disabled:opacity-30
                "
              >
                <FiRefreshCw
                  size={16}
                />
              </button>

            </div>

          </div>


          {/* =================================
              PRICE ERROR
          ================================= */}

          {filters.priceMin !==
              "" &&
            filters.priceMax !==
              "" &&
            Number(
              filters.priceMin
            ) >
              Number(
                filters.priceMax
              ) && (

            <p
              className="
                mt-3

                font-kufi
                text-[9px]

                text-red-500
              "
            >
              {t(
                "Minimum price cannot be greater than maximum price"
              )}
            </p>

          )}

        </div>

      </div>


      {/* =================================
          ACTIVE FILTERS
          تبقى ظاهرة حتى لو أغلق
          الفلتر في الجوال
      ================================= */}

      {hasActiveFilters && (

        <div
          className="
            flex
            flex-wrap

            items-center

            gap-2

            mt-3

            md:mt-4
            md:pt-4

            md:border-t
            md:border-color-2/[0.07]
            dark:md:border-white/[0.07]
          "
        >

          <div
            className="
              flex

              items-center

              gap-2

              me-1

              font-kufi
              text-[9px]

              text-color-2/45
              dark:text-white/35
            "
          >
            <FiFilter
              size={12}

              className="
                text-color-1
              "
            />

            {t(
              "Active Filters"
            )}
          </div>


          {/* Search */}

          {initialFilters.search && (

            <FilterChip
              icon={
                <FiSearch
                  size={10}
                />
              }

              label={
                initialFilters.search
              }

              onRemove={() =>
                clearFilter(
                  "search"
                )
              }
            />

          )}


          {/* Category */}

          {initialFilters.categoryId && (

            <FilterChip
              icon={
                <FiTag
                  size={10}
                />
              }

              label={
                selectedCategory
                  ? isArabic
                    ? selectedCategory
                        .titleAr
                    : selectedCategory
                        .titleEn
                  : t(
                      "Category"
                    )
              }

              onRemove={() =>
                clearFilter(
                  "categoryId"
                )
              }
            />

          )}


          {/* Price */}

          {(
            (
              initialFilters.priceMin !==
                "" &&
              initialFilters.priceMin !==
                undefined
            ) ||
            (
              initialFilters.priceMax !==
                "" &&
              initialFilters.priceMax !==
                undefined
            )
          ) && (

            <FilterChip
              label={`${
                initialFilters.priceMin ||
                0
              } - ${
                initialFilters.priceMax ||
                "∞"
              } ${t("EGP")}`}

              onRemove={
                clearPriceRange
              }
            />

          )}


          {/* Sort */}

          {initialFilters.sortBy &&
            initialFilters.sortBy !==
              "default" && (

            <FilterChip
              icon={
                <FiSliders
                  size={10}
                />
              }

              label={
                initialFilters.sortBy ===
                "price_asc"
                  ? t(
                      "Price Low to High"
                    )
                  : t(
                      "Price High to Low"
                    )
              }

              onRemove={() =>
                clearFilter(
                  "sortBy"
                )
              }
            />

          )}


          {/* Clear All */}

          <button
            type="button"

            onClick={
              handleReset
            }

            className="
              flex

              items-center

              gap-1.5

              rounded-full

              border
              border-color-2/10
              dark:border-white/10

              px-3
              py-1.5

              font-kufi
              text-[9px]

              text-color-2/45
              dark:text-white/40

              transition-all

              hover:border-red-500/20
              hover:bg-red-500/[0.05]
              hover:text-red-500
            "
          >
            <FiRefreshCw
              size={10}
            />

            {t(
              "Clear All"
            )}
          </button>

        </div>

      )}

    </div>
  );
}


/* =====================================
    FIELD
====================================== */

function FilterField({
  label,
  icon,
  children,
}) {
  return (
    <div className="w-full">

      <label
        className="
          block

          mb-2

          font-kufi
          font-medium

          text-[10px]
          md:text-[11px]

          text-color-2/60
          dark:text-white/50
        "
      >
        {label}
      </label>


      <div
        className="
          flex

          h-[48px]

          items-center

          overflow-hidden

          rounded-[1rem]

          border
          border-color-2/10
          dark:border-white/10

          bg-back-secondary/60
          dark:bg-back-darkprimery/60

          transition-all

          focus-within:border-color-1/60

          focus-within:shadow-[0_0_0_3px_rgba(165,119,58,.08)]
        "
      >

        <div
          className="
            flex

            w-11
            h-full

            shrink-0

            items-center
            justify-center

            text-color-1
          "
        >
          {icon}
        </div>


        {children}

      </div>

    </div>
  );
}


/* =====================================
    CLEAR
====================================== */

function ClearButton({
  onClick,
}) {
  return (
    <button
      type="button"

      onClick={
        onClick
      }

      className="
        flex

        w-10
        h-full

        shrink-0

        items-center
        justify-center

        text-color-2/30
        dark:text-white/25

        transition-colors

        hover:text-color-1
      "
    >
      <FiX
        size={14}
      />
    </button>
  );
}


/* =====================================
    CHIP
====================================== */

function FilterChip({
  icon,
  label,
  onRemove,
}) {
  return (
    <button
      type="button"

      onClick={
        onRemove
      }

      className="
        inline-flex

        max-w-[210px]

        items-center

        gap-2

        rounded-full

        border
        border-color-1/10

        bg-color-1/10

        px-3
        py-1.5

        font-kufi
        text-[9px]

        text-color-1

        transition-all

        hover:bg-color-1
        hover:text-white
      "
    >

      {icon}


      <span
        className="
          max-w-[150px]

          truncate
        "
      >
        {label}
      </span>


      <FiX
        size={10}
      />

    </button>
  );
}


export default MealsFilter;